/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import {
  getScanVideoNewVideoTriggerLabel,
  getScanVideoUploadTriggerLabel,
} from '@kaiizen/kai-pro-api';

import { Button, type ButtonColorStyle } from '@/Client/Widgets';
import { Spinner } from '@/Client/Widgets/Spinner';
import { UploadIcon } from '@kaiizen/kai-shiki/react';

import {
  ORIGINATION_EMPTY_CTA_STYLE,
} from '../FocusedScopeEditor/scopeVideoOriginationEmptyStateStyles';
import {
  NEW_VIDEO_BUTTON_ACTIVE_STYLE,
  NEW_VIDEO_BUTTON_STYLE,
} from '../FocusPanel/focusPanelNewVideoButtonStyles';

const TRIGGER_CONTENT_STYLE = css`
  label: SCAN_VIDEO_UPLOAD_TRIGGER_CONTENT;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export type AIScanVideoUploadTriggerVariant = 'projectCard' | 'emptyStateCta' | 'focusPanelChip';

export type AIScanVideoActiveWorkTriggerState = {
  activeCount: number;
};

type AIScanVideoUploadTriggerButtonProps = {
  variant: AIScanVideoUploadTriggerVariant;
  activeCount: number;
  onClick: () => void;
  disabled?: boolean;
  /** Used for project-card and empty-state variants. Defaults to "Upload Videos". */
  idleLabel?: string;
  buttonStyle?: ButtonColorStyle;
  flex?: number;
};

export const AIScanVideoUploadTriggerButton = ({
  variant,
  activeCount,
  onClick,
  disabled = false,
  idleLabel = 'Upload Videos',
  buttonStyle = 'yellow',
  flex,
}: AIScanVideoUploadTriggerButtonProps) => {
  const showLoader = activeCount > 0;

  if (variant === 'projectCard') {
    return (
      <Button buttonStyle={buttonStyle} onClick={onClick} disabled={disabled} flex={flex}>
        <span css={TRIGGER_CONTENT_STYLE}>
          {showLoader && (
            <span aria-hidden={'true'}>
              <Spinner size={14} />
            </span>
          )}
          {getScanVideoUploadTriggerLabel(idleLabel, activeCount)}
        </span>
      </Button>
    );
  }

  if (variant === 'emptyStateCta') {
    return (
      <button
        type={'button'}
        css={ORIGINATION_EMPTY_CTA_STYLE}
        data-testid={'scope-video-origination-upload-button'}
        disabled={disabled}
        onClick={onClick}
      >
        <span css={TRIGGER_CONTENT_STYLE}>
          {showLoader
            ? (
              <span aria-hidden={'true'}>
                <Spinner size={13} />
              </span>
            )
            : <UploadIcon size={13} color={'currentColor'} aria-hidden={'true'} />}
          {getScanVideoUploadTriggerLabel(idleLabel, activeCount)}
        </span>
      </button>
    );
  }

  return (
    <button
      type={'button'}
      css={[NEW_VIDEO_BUTTON_STYLE, showLoader && NEW_VIDEO_BUTTON_ACTIVE_STYLE]}
      title='Upload walkthrough videos and let Kai add to the scope'
      disabled={disabled}
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
    >
      <span css={TRIGGER_CONTENT_STYLE}>
        {showLoader && (
          <span aria-hidden={'true'}>
            <Spinner size={11} />
          </span>
        )}
        {getScanVideoNewVideoTriggerLabel(activeCount)}
      </span>
    </button>
  );
};
